<template>
  <div class="file-upload-component">
    <!-- 隐藏的文件输入框 -->
    <input 
      type="file" 
      ref="fileInput" 
      @change="handleFileUpload" 
      :accept="accept"
      :multiple="multiple"
      style="display: none"
    >
    
    <!-- 上传进度模态框 -->
    <div v-if="showProgress" class="upload-modal-overlay" >
      <div class="upload-modal-content" @click.stop>
        <div class="modal-header">
          <svg class="modal-icon" viewBox="0 0 24 24" width="24" height="24">
            <path fill="currentColor" d="M19.35,10.04C18.67,6.59 15.64,4 12,4C9.11,4 6.6,5.64 5.35,8.04C2.34,8.36 0,10.91 0,14C0,17.31 2.69,20 6,20H19C21.76,20 24,17.76 24,15C24,12.36 21.95,10.22 19.35,10.04Z"/>
          </svg>
          <h3>文件上传</h3>
          <button class="close-btn" @click="cancelUpload">
            <i class="el-icon-close"></i>
          </button>
        </div>
        
        <div class="upload-modal-body">
          <!-- 文件信息 -->
          <div class="file-info">
            <div class="file-icon">
              <i :class="getFileIconClass()"></i>
            </div>
            <div class="file-details">
              <div class="file-name-container">
                <div class="file-name" :title="currentFileName">{{ currentFileName }}</div>
              </div>
              <div class="file-size">{{ formatFileSize(currentFileSize) }}</div>
            </div>
          </div>
          
          <!-- 进度条 -->
          <div class="progress-container">
            <div class="progress-bar">
              <div class="progress-fill" :style="{ width: uploadProgress + '%', backgroundColor: getProgressColor() }"></div>
            </div>
            <div class="progress-text">
              {{ Math.round(uploadProgress) }}%
            </div>
          </div>
          
          <!-- 上传详情 -->
          <div class="upload-details">
            <div class="detail-item">
              <span class="detail-label">状态:</span>
              <span class="detail-value" :class="`status-${uploadStatusClass}`">{{ uploadStatus }}</span>
            </div>
            <div class="detail-item">
              <span class="detail-label">速度:</span>
              <span class="detail-value">{{ uploadSpeed }}</span>
            </div>
          </div>
        </div>
        
        <div class="upload-modal-footer">
          <button @click="cancelUpload" class="cancel-btn">取消上传</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { chunk_upload_path, cancel_upload_path } from '@/js/api_paths';
import { compressImageToWebP, isImageFile } from '@/js/tools';

export default {
  name: 'FileUpload',
  props: {
    accept: {
      type: String,
      default: '.md,.markdown,.txt,.pdf,image/*'
    },
    multiple: {
      type: Boolean,
      default: false
    },
    targetDir: {
      type: String,
      required: true
    },
    compressImages: {
      type: Boolean,
      default: true
    },
    maxSizeMB: {
      type: Number,
      default: 10
    },
    chunkSize: {
      type: Number,
      default: 0.2 * 1024 * 1024
    }
  },
  data() {
    return {
      showProgress: false,
      uploadProgress: 0,
      uploadedChunks: 0,
      totalChunks: 0,
      uploadId: null,
      uploadCancelled: false,
      uploadController: null,
      currentFileName: '',
      currentFileSize: 0,
      startTime: null,
      lastChunkTime: null,
      uploadSpeed: '0 KB/s',
      uploadStatus: '准备中...',
      uploadStatusClass: 'pending',
      uploadSpeedHistory: []
    }
  },
  methods: {
    triggerUpload() {
      this.$refs.fileInput.click();
    },

    async handleFileUpload(event) {
      const files = this.multiple ? Array.from(event.target.files) : [event.target.files[0]];
      
      if (!files || files.length === 0) return;

      const validFiles = files.filter(file => {
        if (!file) return false;
        if (file.size > this.maxSizeMB * 1024 * 1024) {
          alert(`文件大小不能超过${this.maxSizeMB}MB`);
          return false;
        }
        return true;
      });

      if (validFiles.length === 0) return;

      await this.uploadSingleFileWithChunks(validFiles[0]);
      event.target.value = '';
    },

    // 分块上传文件
    async uploadSingleFileWithChunks(file) {
      this.uploadCancelled = false;
      this.showProgress = true;
      this.uploadProgress = 0;
      this.uploadedChunks = 0;
      this.currentFileName = file.name;
      this.currentFileSize = file.size;
      this.startTime = Date.now();
      this.lastChunkTime = Date.now();
      this.uploadSpeed = '0 KB/s';
      this.uploadStatus = '准备中...';
      this.uploadStatusClass = 'pending';
      this.uploadSpeedHistory = [];

      this.uploadId = Date.now().toString() + Math.random().toString(36).substr(2);
      
      try {
        let uploadFile = file;
        
        if (this.compressImages && isImageFile(file)) {
          this.uploadStatus = '正在压缩图片...';
          this.uploadStatusClass = 'processing';
          try {
            uploadFile = await compressImageToWebP(file, {
              maxSizeMB: 10,
              maxWidthOrHeight: 1920,
              useWebWorker: true,
              fileType: 'image/webp',
              quality: 0.8
            });
            
            const originalName = file.name.replace(/\.[^/.]+$/, "");
            this.currentFileName = `${originalName}.webp`;
            this.currentFileSize = uploadFile.size;
          } catch (compressError) {
            console.warn('图片压缩失败，使用原始文件上传:', compressError);
            uploadFile = file;
          }
        }
        
        this.totalChunks = Math.ceil(uploadFile.size / this.chunkSize);
        this.uploadStatus = '开始上传...';
        this.uploadStatusClass = 'uploading';

        // const fileSizeMB = (uploadFile.size / (1024 * 1024)).toFixed(2);
        // console.log(`开始上传文件: ${this.currentFileName}, 大小: ${uploadFile.size} bytes (${fileSizeMB} MB), 分为 ${this.totalChunks} 块`);
        
        for (let i = 0; i < this.totalChunks; i++) {
          if (this.uploadCancelled) {
            await this.cancelUploadRequest();
            break;
          }
          
          const start = i * this.chunkSize;
          const end = Math.min(start + this.chunkSize, uploadFile.size);
          const chunk = uploadFile.slice(start, end);
          
          const formData = new FormData();
          formData.append('file', chunk, `chunk_${i}`);
          formData.append('sessionId', this.uploadId);
          formData.append('fileName', this.currentFileName);
          formData.append('relative_dir', this.targetDir);
          formData.append('chunkIndex', i.toString());
          formData.append('totalChunks', this.totalChunks.toString());
          formData.append('totalSize', uploadFile.size.toString());
          
          const success = await this.uploadChunk(formData, i);
          
          if (!success) {
            this.$emit('upload-error', file, { msg: `上传块 ${i} 失败` });
            return;
          }
          
          this.uploadedChunks = i + 1;
          this.uploadProgress = (this.uploadedChunks / this.totalChunks) * 100;
          
          // 更新上传速度
          this.updateUploadMetrics();
        }
        
        if (!this.uploadCancelled) {
          this.uploadProgress = 100;
          this.uploadStatus = '上传完成';
          this.uploadStatusClass = 'success';
          this.uploadSpeed = '完成';
          
          setTimeout(() => {
            this.showProgress = false;
            this.$emit('upload-success');
          }, 1000);
        }
        
      } catch (error) {
        console.error('文件上传失败:', error);
        this.uploadStatus = '上传失败';
        this.uploadStatusClass = 'error';
        this.showProgress = false;
        this.$emit('upload-error', file, { msg: error.message });
      }
    },

    // 实时更新上传的状态信息
    updateUploadMetrics() {
      const now = Date.now();
      const elapsed = (now - this.lastChunkTime) / 1000; // 秒
      const chunkSizeMB = this.chunkSize / (1024 * 1024);
      const currentSpeed = chunkSizeMB / elapsed;
      
      this.uploadSpeedHistory.push(currentSpeed);
      if (this.uploadSpeedHistory.length > 5) {
        this.uploadSpeedHistory.shift();
      }
      
      // 计算平均速度
      const avgSpeed = this.uploadSpeedHistory.reduce((a, b) => a + b, 0) / this.uploadSpeedHistory.length;
      this.uploadSpeed = `${(avgSpeed).toFixed(2)} MB/s`;
      
      this.lastChunkTime = now;
    },

    // 上传文件块
    async uploadChunk(formData, chunkIndex) {
      try {
        this.uploadController = new AbortController();
        
        const token = this.$store.state.token;
        const response = await fetch(chunk_upload_path, {
          method: 'POST',
          body: formData,
          headers: {
            'Authorization': `Bearer ${token}`
          },
          signal: this.uploadController.signal
        });
        
        const data = await response.json();
        return response.ok && data.status === "success";
      } catch (error) {
        if (error.name === 'AbortError') {
          return false;
        }
        console.error(`上传块 ${chunkIndex} 失败:`, error);
        return false;
      }
    },

    async cancelUpload() {
      this.uploadCancelled = true;
      if (this.uploadController) {
        this.uploadController.abort();
      }
      await this.cancelUploadRequest();
      this.showProgress = false;
      // this.$emit('upload-cancelled');
    },
    
    // 终止文件上传
    async cancelUploadRequest() {
      try {
        const token = this.$store.state.token;
        const sessionId = this.uploadId;
        
        await fetch(cancel_upload_path, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            sessionId: sessionId
          })
        });
        
        console.log('取消上传请求成功:', sessionId);
      } catch (error) {
        console.error('取消上传请求失败:', error);
      }
    },
    
    getFileIconClass() {
      const ext = this.currentFileName.split('.').pop().toLowerCase();
      const iconMap = {
        'jpg': 'el-icon-picture',
        'jpeg': 'el-icon-picture',
        'png': 'el-icon-picture',
        'gif': 'el-icon-picture',
        'webp': 'el-icon-picture',
        'pdf': 'el-icon-document',
        'txt': 'el-icon-document',
        'md': 'el-icon-document',
        'markdown': 'el-icon-document',
        'doc': 'el-icon-document',
        'docx': 'el-icon-document',
        'xls': 'el-icon-document',
        'xlsx': 'el-icon-document',
        'zip': 'el-icon-folder',
        'rar': 'el-icon-folder',
        'default': 'el-icon-document'
      };
      return iconMap[ext] || iconMap.default;
    },

    formatFileSize(bytes) {
      if (bytes === 0) return '0 Bytes';
      const k = 1024;
      const sizes = ['Bytes', 'KB', 'MB', 'GB'];
      const i = Math.floor(Math.log(bytes) / Math.log(k));
      return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    },

    getProgressColor() {
      if (this.uploadStatusClass === 'success') return '#4CAF50';
      if (this.uploadStatusClass === 'error') return '#f44336';
      if (this.uploadStatusClass === 'uploading') return '#2196F3';
      return '#FF9800';
    },

    resetInput() {
      if (this.$refs.fileInput) {
        this.$refs.fileInput.value = '';
      }
    }
  },
  beforeDestroy() {
    if (!this.uploadCancelled && this.uploadController) {
      this.uploadController.abort();
    }
  }
}
</script>

<style scoped>
.file-upload-component {
  display: inline-block;
}

/* 模态框遮罩层 */
.upload-modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.4);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 2000;
  backdrop-filter: blur(2px);
}

/* 模态框内容 */
.upload-modal-content {
  background: linear-gradient(145deg, #ffffff, #f8f9fa);
  border-radius: 12px;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.15), 0 0 0 1px rgba(0, 0, 0, 0.05);
  width: 500px;
  max-width: 95%;
  animation: modalFadeIn 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  display: flex;
  flex-direction: column;
}

@keyframes modalFadeIn {
  from {
    opacity: 0;
    transform: scale(0.95) translateY(-20px);
  }
  to {
    opacity: 1;
    transform: scale(1) translateY(0);
  }
}

/* 模态框头部 */
.modal-header {
  padding: 20px 24px;
  display: flex;
  align-items: center;
  background: linear-gradient(120deg, #8a2be2 0%, #9370db 100%);
  color: white;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  flex-shrink: 0; /* 防止头部被压缩 */
}

.modal-icon {
  color: rgba(255, 255, 255, 0.9);
  margin-right: 12px;
  filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.2));
}

.modal-header h3 {
  margin: 0;
  font-size: 18px;
  font-weight: 500;
  letter-spacing: 0.5px;
  flex: 1;
}

.close-btn {
  background: transparent;
  border: none;
  color: rgba(255, 255, 255, 0.8);
  cursor: pointer;
  font-size: 20px;
  padding: 4px;
  border-radius: 4px;
  transition: all 0.2s;
  margin-left: auto;
}

.close-btn:hover {
  background: rgba(255, 255, 255, 0.1);
  color: white;
}

/* 模态框主体 */
.upload-modal-body {
  padding: 20px;
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 0; /* 重要：允许子元素正确计算高度 */
}

/* 文件信息 */
.file-info {
  display: flex;
  align-items: center;
  margin-bottom: 20px;
  padding: 12px;
  background-color: #f8f9fa;
  border-radius: 6px;
  border: 1px solid #e9ecef;
}

.file-icon {
  font-size: 24px;
  margin-right: 12px;
  color: #8a2be2;
  flex-shrink: 0; /* 防止图标被压缩 */
}

.file-details {
  flex: 1;
  display: flex;
  flex-direction: column;
  min-width: 0; /* 启用弹性收缩 */
}

.file-name-container {
  display: flex;
  align-items: center;
  margin-bottom: 4px;
  min-width: 0; /* 启用弹性收缩 */
}

.file-name {
  font-weight: 500;
  color: #333;
  font-size: 14px;
  font-family: 'Monaco', 'Consolas', monospace;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  flex: 1;
  min-width: 0; /* 启用弹性收缩 */
}

/* 为文件名添加悬停显示完整名称 */
.file-name:hover {
  overflow: visible;
  position: relative;
  z-index: 1000;
}

.file-size {
  font-size: 12px;
  color: #666;
  flex-shrink: 0; /* 防止文件大小被压缩 */
}

/* 进度条容器 */
.progress-container {
  margin-bottom: 15px;
}

.progress-bar {
  width: 100%;
  height: 12px;
  background-color: #e0e0e0;
  border-radius: 6px;
  overflow: hidden;
  margin-bottom: 8px;
  position: relative;
  border: 1px solid #dcdfe6;
}

.progress-fill {
  height: 100%;
  background: linear-gradient(135deg, #8a2be2 0%, #6a0dad 100%);
  transition: width 0.3s ease, background-color 0.3s ease;
  border-radius: 6px;
  position: relative;
}

.progress-fill::after {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(
    45deg,
    rgba(255, 255, 255, 0.2) 25%,
    transparent 25%,
    transparent 50%,
    rgba(255, 255, 255, 0.2) 50%,
    rgba(255, 255, 255, 0.2) 75%,
    transparent 75%,
    transparent
  );
  background-size: 20px 20px;
  animation: progressAnimation 1s linear infinite;
  border-radius: 6px;
}

@keyframes progressAnimation {
  0% {
    background-position: 0 0;
  }
  100% {
    background-position: 20px 20px;
  }
}

.progress-text {
  text-align: center;
  font-size: 14px;
  font-weight: 500;
  color: #333;
  margin-bottom: 15px;
}

/* 上传详情 */
.upload-details {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
  margin-bottom: 15px;
}

.detail-item {
  display: flex;
  justify-content: space-between;
  padding: 6px 0;
  border-bottom: 1px solid #f0f0f0;
}

.detail-label {
  color: #6c757d;
  font-size: 12px;
  font-weight: 500;
}

.detail-value {
  color: #606266;
  font-size: 12px;
  font-weight: 500;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.detail-value.status-success {
  color: #67c23a;
}

.detail-value.status-error {
  color: #f56c6c;
}

.detail-value.status-pending {
  color: #e6a23c;
}

.detail-value.status-processing {
  color: #409eff;
}

.detail-value.status-uploading {
  color: #409eff;
}

/* 模态框底部 */
.upload-modal-footer {
  padding: 16px 20px;
  border-top: 1px solid #e9ecef;
  text-align: right;
  background: #f8f9fa;
  border-radius: 0 0 12px 12px;
  flex-shrink: 0; /* 防止底部被压缩 */
}

.cancel-btn {
  padding: 8px 16px;
  background: linear-gradient(135deg, #f56c6c 0%, #e74c3c 100%);
  color: white;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  transition: all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
  box-shadow: 0 2px 6px rgba(245, 108, 108, 0.3);
}

.cancel-btn:hover:not(:disabled) {
  background: linear-gradient(135deg, #e74c3c 0%, #c0392b 100%);
  box-shadow: 0 4px 12px rgba(245, 108, 108, 0.4);
  transform: translateY(-1px);
}

.cancel-btn:disabled {
  background: #cccccc;
  cursor: not-allowed;
  box-shadow: none;
  transform: none;
}

/* 响应式设计 */
@media (max-width: 480px) {
  .upload-modal-content {
    width: 95%;
    margin: 10px;
  }
  
  .upload-details {
    grid-template-columns: 1fr;
  }
  
  .modal-header h3 {
    font-size: 16px;
  }
  
  .upload-modal-body {
    padding: 15px;
  }
  
  .file-name {
    font-size: 12px;
  }
  
  .file-size {
    font-size: 11px;
  }
}
</style>